import { useHead } from "@unhead/vue";
import { allLocales, type Locale } from "shared-core/services/i18n.ts";
import {
  type LocationQuery,
  type RouteLocationNormalizedLoaded,
  useRouter,
} from "vue-router";
import type { RouteNamedMap } from "vue-router/auto-routes";

export function getAlternateLocalePath(
  route: RouteLocationNormalizedLoaded<keyof RouteNamedMap>,
  currentLocale: Locale,
): { path: string; query: LocationQuery; hash: string } {
  const next = currentLocale === "en" ? "sv" : "en";
  const { path, query, hash } = route;

  // If URL starts with /en or /sv, replace it; otherwise prefix it
  return {
    path: /^\/(?:en|sv)(?=\/|$)/.test(path)
      ? path.replace(/^\/(?:en|sv)(?=\/|$)/, `/${next}`)
      : `/${next}${path === "/" ? "" : path}`,
    query,
    hash,
  };
}

export function useHrefLang(
  route: RouteLocationNormalizedLoaded<keyof RouteNamedMap>,
  currentLocale: Locale,
): void {
  const links: {
    rel: "alternate";
    hreflang: Locale | "x-default";
    href: string;
  }[] = [];

  const alternateLocalePath = getAlternateLocalePath(route, currentLocale);
  const router = useRouter();

  if (currentLocale === "en") {
    links.push({
      rel: "alternate",
      hreflang: "x-default",
      href: import.meta.env.VITE_PUBLIC_SITE_URL + route.fullPath,
    });
  } else {
    links.push({
      rel: "alternate",
      hreflang: "x-default",
      href:
        import.meta.env.VITE_PUBLIC_SITE_URL +
        router.resolve(alternateLocalePath).fullPath,
    });
  }

  allLocales.forEach((locale: Locale) => {
    links.push({
      rel: "alternate",
      hreflang: locale,
      href:
        import.meta.env.VITE_PUBLIC_SITE_URL +
        (currentLocale === locale ? route.fullPath : alternateLocalePath.path),
    });
  });

  useHead({ link: links });
}
